import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { GeneratedLinkUrlObject, SearchPageQueryType } from 'src/utils/links';
import SortingDropdown, {
  ProposalsSortBy,
  SortBy,
  SortDirection,
  SortList
} from 'src/shared-components/SortingDropdown';
import dynamic from 'next/dynamic';

import { GTAGPrefix } from 'src/hooks/useGTAG';
import useMedia from '../../../hooks/useMedia';
import {
  ModalPosition,
  ModalRounding
} from '../../../shared-components/Modal/Modal';
import { BlogSortBy } from '../../../api/types';
import useModalStore from '../../../hooks/useModalStore';

const Modal = dynamic(() => import('src/shared-components/Modal/Modal'), {
  ssr: false
});

interface Props {
  parentElementRef?: React.MutableRefObject<HTMLElement>;
  pageLinkGenerationMethod: (
    query: SearchPageQueryType
  ) => GeneratedLinkUrlObject;
  textSearch?: string;
  gtagPrefix?: GTAGPrefix;
  sortList?: BlogSortBy[];
  isDisplayCustomSortList?: boolean;
}

const SortingSelect: FC<Props> = ({
  parentElementRef,
  pageLinkGenerationMethod,
  textSearch,
  gtagPrefix,
  sortList,
  isDisplayCustomSortList
}) => {
  const router = useRouter();
  const { m } = useMedia();
  const { updateModalsConfig } = useModalStore();
  const defaultList = [
    SortBy.price,
    SortBy.name,
    SortBy.popularity,
    SortBy.relevance
  ];
  const listSortDropdown = isDisplayCustomSortList ? sortList : defaultList;

  const [sortValue, setSortValue] = useState<{
    sortBy: SortBy | ProposalsSortBy;
    sortDirection: SortDirection;
  }>({ sortBy: undefined, sortDirection: undefined });

  const handleSortingChange = ({ sortBy, sortDirection }) => {
    updateModalsConfig('sorting-select', false);
    setSortValue({ sortBy, sortDirection });
    router.push(
      pageLinkGenerationMethod({
        ...router.query,
        page: 1,
        // No need to provide any sorting parameters in case of sorting by relevance. It's a default sorting option
        // Provide null or undefined to remove the query param from the URL
        ...(sortBy === SortBy.relevance
          ? { sortBy: null, sortDirection: null }
          : {
              sortBy,
              sortDirection
            }),
        ...(textSearch && { search: textSearch })
      }),
      undefined,
      { scroll: false }
    );
  };

  useEffect(() => {
    const { sortBy } = router.query;
    const { sortDirection } = router.query;
    if (sortBy && sortDirection) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setSortValue({ sortBy, sortDirection });
    }
  }, [router.query.sortBy, router.query.sortDirection]);

  return (
    <>
      <SortingDropdown
        proposalsSortList={listSortDropdown}
        proposalSortProps={sortValue}
        onProposalsSortChange={handleSortingChange}
        onToggle={(newState) => updateModalsConfig('sorting-select', newState)}
        disabled={!m}
        gtagPrefix={gtagPrefix}
      />
      {!m && (
        <Modal
          withCloseIcon={true}
          position={ModalPosition.RelativeBottom}
          roundingMode={ModalRounding.Bottom}
          modalContainerId="modal-root"
          onClose={() => updateModalsConfig('sorting-select', false)}
          triggerElementRef={parentElementRef}
          childrenContainerClass="px-0"
          id="sorting-select"
        >
          <SortList
            proposalsSortList={listSortDropdown}
            selectedProposalsSort={sortValue}
            onProposalsSortChange={handleSortingChange}
          />
        </Modal>
      )}
    </>
  );
};

export default SortingSelect;
